/* ------------------------------------------------------------------ *
 * Server shapes
 * ------------------------------------------------------------------ */

export type SettingSource = "default" | "env" | "appConfig";

export interface RateLimitSetting {
  key: string;
  value: string;
  source: SettingSource | string;
}

export interface RateLimitStatus {
  enabled: boolean;
  redisHealthy: boolean;
  settings: Array<RateLimitSetting>;
}

/* ------------------------------------------------------------------ *
 * OTP block lookup — `otpBlockStatus` / `unblockOtpRecipient`
 * ------------------------------------------------------------------ */

/** Which axis a counter is keyed on, as reported by the server. */
export type Dimension =
  | "ip"
  | "subject"
  | "device"
  | "destination"
  | "arg"
  | "global"
  | "unknown";

/** How the server decided to read the string the admin typed. */
export type IdentifierKind = "ip" | "email" | "phone" | "device";

export interface RateLimitCounter {
  tier: string;
  dimension: Dimension;
  value: string;
  window: string;
  used: number;
  limit: number;
  remaining: number;
  /**
   * Seconds from the moment of the lookup — a countdown, not a timestamp. It
   * is stale by design; see `formatCountdown`.
   */
  resetsInSeconds: number;
  blocking: boolean;
}

export interface OtpCorrelation {
  destinations: number;
  ipAddresses: number;
  deviceCount: number;
  windowHours: number;
  otpSendRows: number;
}

export interface OtpBlockStatus {
  identifier: string;
  kind: IdentifierKind | string;
  blocked: boolean;
  /**
   * True when the identifier fans out to more members than the server will
   * clear in one call. The UI must not offer a plain unblock in that case —
   * the operation is narrower than the button implies. See `OtpUnblockPanel`.
   */
  cascadeWouldBeRefused: boolean;
  counters: Array<RateLimitCounter>;
  correlation: OtpCorrelation | null;
}

export interface UnblockOtpResult {
  kind: IdentifierKind | string;
  /**
   * Redis keys deleted — one per window per tier. NOT requests, attempts or
   * people: a single member routinely accounts for a dozen. Never label this
   * as anything but counters; the number ends up in the audit trail.
   */
  countersCleared: number;
  cascaded: boolean;
  destinationsCleared: number;
  ipAddressesCleared: number;
  devicesCleared: number;
  accountsCleared: number;
  /** Members the server refused to cascade to. Must be surfaced — see below. */
  skippedDestinations: Array<string>;
  registrationBackstopCleared: boolean;
}

/**
 * The counter the verdict line quotes.
 *
 * `counters` arrives pre-sorted — blocking first, then closest to the limit —
 * so this is row one whenever anything blocks. Located by predicate rather
 * than by index so that a server which stopped sorting would degrade to a
 * right answer rather than a confident wrong one. Do not re-sort the array
 * for display: server order *is* the answer order.
 */
export function firstBlockingCounter(
  counters: Array<RateLimitCounter>
): RateLimitCounter | null {
  return counters.find((counter) => counter.blocking) ?? null;
}

export function pluralize(
  count: number,
  noun: string,
  plural = `${noun}s`
): string {
  return `${count} ${count === 1 ? noun : plural}`;
}

/**
 * `289` -> `4m 49s`.
 *
 * Formatted once, at lookup time, and deliberately never re-rendered on a
 * tick: a ticking clock implies live data, which would need polling, and this
 * screen's own reads are metered at 5/min. Stale by a few seconds is fine and
 * far cheaper than the admin throttling themselves out of the dashboard.
 */
export function formatCountdown(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "now";

  const seconds = Math.floor(totalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  const parts: Array<string> = [];

  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  // Seconds are noise once we are quoting hours.
  if (remainder && !hours) parts.push(`${remainder}s`);

  return parts.join(" ") || "now";
}

/** "1 destination · 1 IP · 1 device, from 28 OTP sends in the last 24h". */
export function describeCorrelation(correlation: OtpCorrelation): string {
  const fanout = [
    pluralize(correlation.destinations, "destination"),
    pluralize(correlation.ipAddresses, "IP"),
    pluralize(correlation.deviceCount, "device"),
  ].join(" · ");

  return (
    `${fanout}, from ${pluralize(correlation.otpSendRows, "OTP send")} ` +
    `in the last ${correlation.windowHours}h`
  );
}

/**
 * "Cleared 18 counters across 2 destinations, 1 IP, 1 device, 2 accounts."
 *
 * Says "counters" on purpose — see `UnblockOtpResult.countersCleared`.
 */
export function summarizeUnblock(result: UnblockOtpResult): string {
  const scope = [
    pluralize(result.destinationsCleared, "destination"),
    pluralize(result.ipAddressesCleared, "IP"),
    pluralize(result.devicesCleared, "device"),
    pluralize(result.accountsCleared, "account"),
  ].join(", ");

  return `Cleared ${pluralize(
    result.countersCleared,
    "counter"
  )} across ${scope}.`;
}

/* ------------------------------------------------------------------ *
 * Keys
 * ------------------------------------------------------------------ */

export const ENABLED_KEY = "rateLimit.enabled";
export const REGISTRATION_IP_KEY = "rateLimit.tier.registration.ip";
export const REGISTRATION_DEVICE_KEY = "rateLimit.tier.registration.device";

/**
 * Keys this panel must never write.
 *
 * `registration.maxPerIpPerDay`, `registration.maxPerDevicePerDay` and
 * `registration.windowMinutes` exist server-side and silently *override* the
 * `rateLimit.tier.registration.*` rows below — but they are not returned by
 * `rateLimitStatus`, so the panel cannot show their current state. Writing one
 * would make this screen lie about what is enforced.
 *
 * `rateLimit.allowlistCidrs` is writable but likewise not readable, so a field
 * for it could never display its own value. Left out on purpose.
 */
export const UNWRITABLE_KEYS = [
  "registration.maxPerIpPerDay",
  "registration.maxPerDevicePerDay",
  "registration.windowMinutes",
  "rateLimit.allowlistCidrs",
];

const TIER_KEY_PATTERN = /^rateLimit\.tier\.([^.]+)\.([^.]+)$/;

export interface TierKeyParts {
  tier: string;
  dimension: string;
}

/** Splits `rateLimit.tier.auth-otp-send.ip` into its tier and dimension. */
export function parseTierKey(key: string): TierKeyParts | null {
  const match = TIER_KEY_PATTERN.exec(key);

  if (!match) return null;

  return { tier: match[1], dimension: match[2] };
}

/* ------------------------------------------------------------------ *
 * Value format: `count/window`, comma-separated, all windows apply at once
 * ------------------------------------------------------------------ */

export type WindowUnit = "s" | "m" | "h" | "d";

export interface LimitWindow {
  /** Kept as strings so the inputs stay controlled while being typed. */
  count: string;
  amount: string;
  unit: WindowUnit;
}

/** The server's accepted syntax, e.g. `3/1h,5/1d`. */
export const LIMIT_VALUE_PATTERN =
  /^\d+\s*\/\s*\d+[smhd](\s*,\s*\d+\s*\/\s*\d+[smhd])*$/;

export const UNIT_LABELS: Record<WindowUnit, { one: string; many: string }> = {
  s: { one: "second", many: "seconds" },
  m: { one: "minute", many: "minutes" },
  h: { one: "hour", many: "hours" },
  d: { one: "day", many: "days" },
};

export const UNIT_OPTIONS: Array<{ value: WindowUnit; label: string }> = [
  { value: "s", label: "seconds" },
  { value: "m", label: "minutes" },
  { value: "h", label: "hours" },
  { value: "d", label: "days" },
];

/**
 * Parses a stored value into editable windows, or `null` when the value is not
 * in a syntax this widget can round-trip.
 *
 * A `null` here is informative rather than broken — see `UNEDITABLE_VALUE_HINT`.
 */
export function parseLimitValue(value: string): Array<LimitWindow> | null {
  if (!LIMIT_VALUE_PATTERN.test(value.trim())) return null;

  return value.split(",").map((part) => {
    const [count, window] = part.split("/").map((piece) => piece.trim());

    return {
      count,
      amount: window.slice(0, -1),
      unit: window.slice(-1) as WindowUnit,
    };
  });
}

export function serializeLimitWindows(windows: Array<LimitWindow>): string {
  return windows
    .map(({ count, amount, unit }) => `${count.trim()}/${amount.trim()}${unit}`)
    .join(",");
}

/**
 * Client-side gate for every save. The mutation accepts anything, so an admin
 * typing `5 per day` would otherwise get a success toast and no enforcement.
 * Returns an error message, or `null` when the windows are safe to send.
 */
export function validateLimitWindows(
  windows: Array<LimitWindow>
): string | null {
  if (!windows.length) return "Add at least one window.";

  for (const { count, amount } of windows) {
    if (!/^\d+$/.test(count.trim())) {
      return "Count must be a whole number.";
    }

    if (!/^\d+$/.test(amount.trim())) {
      return "Window length must be a whole number.";
    }

    if (Number(amount.trim()) < 1) {
      return "Window length must be at least 1.";
    }
  }

  if (!LIMIT_VALUE_PATTERN.test(serializeLimitWindows(windows))) {
    return "Use the format count/window, e.g. 3/1h,5/1d.";
  }

  return null;
}

/** `3/1h,5/1d` -> "3 per hour and 5 per day". */
export function describeLimitValue(value: string): string {
  const windows = parseLimitValue(value);

  if (!windows) return value;

  return windows.map(describeWindow).join(" and ");
}

export function describeWindow({ count, amount, unit }: LimitWindow): string {
  const size = Number(amount);
  const labels = UNIT_LABELS[unit];
  const window = size === 1 ? labels.one : `${size} ${labels.many}`;

  return `${count} per ${window}`;
}

/**
 * Shown when a tier row comes back in a syntax the widget cannot edit.
 *
 * This used to blame the legacy `registration.*` keys, which rendered as
 * `5/1440min` — an invalid unit. The server now describes that override as
 * `5/1d`, so an unparseable value no longer implies anything about
 * registration; see `REGISTRATION_OVERRIDE_HINT` for that case.
 *
 * The consequence is worth stating outright: the server does not reject a
 * value it cannot parse, it ignores it and falls back to the tier's compiled
 * default. An unparseable row is therefore not "a limit we can't display" but
 * "probably not the limit being enforced".
 */
export const UNEDITABLE_VALUE_HINT =
  "This value is not in the count/window syntax this screen edits — e.g. 10/1h,5/1d, with units s, m, h or d. It was " +
  "set outside this panel, and the server does not reject syntax it cannot parse: it ignores the value and falls back to " +
  "the tier's built-in default. This limit is most likely not being enforced as written. Fix it server-side.";

/**
 * The standing caveat on the registration rows.
 *
 * These keys override `rateLimit.tier.registration.*` when they exist, but
 * `rateLimitStatus` does not return them and `source` still reports
 * `appConfig`, so there is nothing in the payload that distinguishes an
 * overridden row from a normal one. The panel cannot detect this — it can only
 * say so, and catch the revert after the fact via `saveLooksReverted`. The
 * keys themselves are the first three entries of `UNWRITABLE_KEYS` above.
 */
export const REGISTRATION_OVERRIDE_HINT =
  "The legacy keys registration.maxPerIpPerDay, registration.maxPerDevicePerDay and registration.windowMinutes override " +
  "these two rows whenever they exist in the database. They are not returned by rateLimitStatus and do not change the " +
  "source chip, so this panel can show the effective number but cannot tell that something else is setting it. Newer " +
  "backends no longer create them; older environments may still have them. Clear them server-side to manage the limit here.";

/**
 * How long to trust "saved, still propagating" before calling a write failed.
 *
 * Each server instance caches settings for ~30s, so a value that still reads
 * old immediately after a save is expected, not broken. Past this grace period
 * it is no longer explainable by caching.
 */
export const SAVE_PROPAGATION_GRACE_MS = 45_000;

/**
 * True when a saved value has been contradicted by the server for longer than
 * caching can explain — the visible symptom of an invisible override.
 *
 * Deliberately time-based rather than refresh-counting: the row does not know
 * when a refetch happened, but it re-renders on every one, so evaluating this
 * at render time catches the revert on the first refresh past the grace period
 * without a timer of its own.
 */
export function saveLooksReverted(
  savedValue: string | null,
  serverValue: string,
  savedAt: number | null,
  now: number = Date.now()
): boolean {
  if (savedValue === null || savedAt === null) return false;
  if (savedValue === serverValue) return false;

  return now - savedAt >= SAVE_PROPAGATION_GRACE_MS;
}

/* ------------------------------------------------------------------ *
 * Source of the effective value
 * ------------------------------------------------------------------ */

type ChipColor = "default" | "info" | "warning";

export const SOURCE_META: Record<
  string,
  { label: string; color: ChipColor; help: string }
> = {
  default: {
    label: "Default",
    color: "default",
    help: "Stock value hardcoded in the server. Nobody has overridden it.",
  },
  env: {
    label: "Env",
    color: "info",
    help: "Set in the server's .env file. Saving here overrides it until the override is removed.",
  },
  appConfig: {
    label: "Override",
    color: "warning",
    help: "Edited in this admin panel, overriding the default and any .env value.",
  },
};

export function sourceMeta(source: string) {
  return (
    SOURCE_META[source] ?? {
      label: source,
      color: "default" as ChipColor,
      help: "Unrecognised source reported by the server.",
    }
  );
}

/* ------------------------------------------------------------------ *
 * Tier catalog — labels and ordering for the grouped views
 * ------------------------------------------------------------------ */

export interface TierMeta {
  label: string;
  description: string;
}

export const TIER_META: Record<string, TierMeta> = {
  registration: {
    label: "Registration",
    description: "Account signups.",
  },
  "global-ip": {
    label: "Global",
    description: "Coarse envelope applied to every request.",
  },
  "auth-otp-send": {
    label: "OTP — send",
    description: "Requesting an SMS or email code.",
  },
  "auth-otp-destination": {
    label: "OTP — per destination",
    description: "Codes sent to a single phone number.",
  },
  "auth-otp-verify": {
    label: "OTP — verify",
    description: "Code-guessing and brute force protection.",
  },
  "token-refresh": {
    label: "Token refresh",
    description: "Token refreshes per user.",
  },
  enumeration: {
    label: "Enumeration",
    description: "Username, email and phone availability checks.",
  },
  "third-party-paid": {
    label: "Third-party (paid)",
    description: "Google Places and Foursquare lookups — these cost money.",
  },
  upload: {
    label: "Uploads",
    description: "File uploads.",
  },
  "admin-export": {
    label: "Admin export",
    description: "CSV exports — and this panel's own calls.",
  },
  broadcast: {
    label: "Broadcast",
    description: "Mass SMS sends.",
  },
  "broadcast-recipient": {
    label: "Broadcast — per recipient",
    description: "Mass SMS, counted per recipient.",
  },
  webhook: {
    label: "Webhooks",
    description: "Zapier and ZegoCloud callbacks.",
  },
  telemetry: {
    label: "Telemetry",
    description: "Client telemetry writes.",
  },
  "default-query": {
    label: "Default query",
    description: "Fallback for queries with no tier of their own.",
  },
  "default-mutation": {
    label: "Default mutation",
    description: "Fallback for mutations with no tier of their own.",
  },
};

/** Order the Advanced section renders in; unknown tiers are appended. */
export const TIER_ORDER = [
  "global-ip",
  "auth-otp-send",
  "auth-otp-destination",
  "auth-otp-verify",
  "token-refresh",
  "enumeration",
  "third-party-paid",
  "upload",
  "admin-export",
  "broadcast",
  "broadcast-recipient",
  "webhook",
  "telemetry",
  "default-query",
  "default-mutation",
];

export function tierMeta(tier: string): TierMeta {
  return TIER_META[tier] ?? { label: tier, description: "" };
}

export const DIMENSION_LABELS: Record<string, string> = {
  ip: "Per IP address",
  device: "Per device",
  destination: "Per destination",
  subject: "Per user",
  arg: "Per submitted value",
  global: "Across all senders",
};

export function dimensionLabel(dimension: string): string {
  return DIMENSION_LABELS[dimension] ?? `Per ${dimension}`;
}

/**
 * Column-width variants of the labels above. `DIMENSION_LABELS` reads as a
 * sentence fragment next to a settings field ("Per IP address"); inside the
 * counter table that phrasing repeats on every row and crowds out the values.
 */
export const DIMENSION_SHORT_LABELS: Record<string, string> = {
  ip: "IP",
  device: "Device",
  destination: "Destination",
  subject: "User",
  arg: "Submitted value",
  global: "Global",
  unknown: "Unknown",
};

export function dimensionShortLabel(dimension: string): string {
  return DIMENSION_SHORT_LABELS[dimension] ?? dimension;
}

export interface TierGroup {
  tier: string;
  meta: TierMeta;
  settings: Array<RateLimitSetting>;
}

/**
 * Groups the tier rows by tier, in `TIER_ORDER`, excluding `excludeTiers`
 * (the registration rows get their own dedicated screen) and any non-tier row
 * such as `rateLimit.enabled`.
 */
export function groupSettingsByTier(
  settings: Array<RateLimitSetting>,
  excludeTiers: Array<string> = []
): Array<TierGroup> {
  const groups = new Map<string, Array<RateLimitSetting>>();

  settings.forEach((setting) => {
    const parts = parseTierKey(setting.key);

    if (!parts || excludeTiers.includes(parts.tier)) return;

    const existing = groups.get(parts.tier);

    if (existing) {
      existing.push(setting);
      return;
    }

    groups.set(parts.tier, [setting]);
  });

  const ordered = Array.from(groups.keys()).sort((a, b) => {
    const aIndex = TIER_ORDER.indexOf(a);
    const bIndex = TIER_ORDER.indexOf(b);

    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });

  return ordered.map((tier) => ({
    tier,
    meta: tierMeta(tier),
    settings: groups.get(tier) ?? [],
  }));
}

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

/**
 * Re-exported from the shared module, which handles all three rejection shapes
 * — this panel used to see only the GraphQL one, and a global per-IP 429 looked
 * to it like an unexplained network failure.
 */
export type { RateLimitRejection } from "../../common/rateLimit";
export {
  getRateLimitRejection,
  formatRetryAfter,
  describeError,
} from "../../common/rateLimit";
